'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridSolidCenterProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridSolidCenter - Clean grid pattern with center fade
 *
 * Continuous 1px lines forming a square grid that fades from the center.
 * Ideal for hero sections where text is centered.
 *
 * Fixed fadeSize: 0.45
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridSolidCenter />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridSolidCenter({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridSolidCenterProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.45
  const size = `${fadeSize * 100}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, #000 40%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
